import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { sendAchievementPostedNotifications } from '../../../hooks/useNotifications';

interface AchievementFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  defaultGroupId?: string;
  editData?: {
    id: string;
    title: string;
    description: string | null;
    achievement_date: string;
    category: string;
    image_url: string | null;
    visibility: 'public' | 'group_only';
    group_id: string | null;
  };
}

interface StudyGroup {
  id: string;
  name: string;
}

const categories = [
  '資格試験',
  '学位取得',
  '論文発表',
  '研究成果',
  '学会発表',
  'コンペティション',
  '参考書読破',
  '過去問演習結果',
  'その他'
];

export default function AchievementForm({ onSuccess, onCancel, defaultGroupId, editData }: AchievementFormProps) {
  const isEditMode = !!editData;

  const [title, setTitle] = useState(editData?.title || '');
  const [description, setDescription] = useState(editData?.description || '');
  const [achievementDate, setAchievementDate] = useState(
    editData?.achievement_date || new Date().toISOString().split('T')[0]
  );
  const [category, setCategory] = useState(editData?.category || categories[0]);
  const [imageUrl, setImageUrl] = useState(editData?.image_url || '');
  const [visibility, setVisibility] = useState<'public' | 'group_only'>(editData?.visibility || 'public');
  const [selectedGroupId, setSelectedGroupId] = useState<string>(
    editData?.group_id || defaultGroupId || ''
  );
  const [myGroups, setMyGroups] = useState<StudyGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMyGroups();
  }, []);

  const fetchMyGroups = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: memberships } = await supabase
        .from('study_group_members')
        .select('group_id')
        .eq('user_id', user.id);

      if (!memberships || memberships.length === 0) {
        setMyGroups([]);
        return;
      }

      const groupIds = memberships.map(m => m.group_id);
      const { data: groups } = await supabase
        .from('study_groups')
        .select('id, name')
        .in('id', groupIds);

      setMyGroups(groups || []);
    } catch (err) {
      console.error('グループの取得エラー:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('タイトルを入力してください');
      return;
    }

    if (visibility === 'group_only' && !selectedGroupId) {
      setError('グループ内限定公開の場合はグループを選択してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('ログインが必要です');
        return;
      }

      if (isEditMode && editData) {
        // 編集モード：UPDATE
        const { error: updateError } = await supabase
          .from('achievements')
          .update({
            title: title.trim(),
            description: description.trim() || null,
            achievement_date: achievementDate,
            category,
            image_url: imageUrl.trim() || null,
            visibility,
            group_id: selectedGroupId || null,
          })
          .eq('id', editData.id)
          .eq('user_id', user.id);

        if (updateError) throw updateError;
      } else {
        // 新規投稿モード：INSERT
        const { data: newAchievement, error: insertError } = await supabase
          .from('achievements')
          .insert({
            user_id: user.id,
            title: title.trim(),
            description: description.trim() || null,
            achievement_date: achievementDate,
            category,
            image_url: imageUrl.trim() || null,
            visibility,
            group_id: selectedGroupId || null
          })
          .select()
          .maybeSingle();

        if (insertError) throw insertError;

        // グループが選択されている場合、グループメンバーに通知を送る
        if (newAchievement && selectedGroupId) {
          const { data: members } = await supabase
            .from('study_group_members')
            .select('user_id')
            .eq('group_id', selectedGroupId);
          const memberIds = (members || []).map((m: { user_id: string }) => m.user_id);
          if (memberIds.length > 0) {
            await sendAchievementPostedNotifications(
              newAchievement.id,
              title.trim(),
              selectedGroupId,
              memberIds,
              user.id
            );
          }
        }
      }

      onSuccess();
    } catch (err) {
      console.error('達成報告の投稿エラー:', err);
      setError(isEditMode ? '更新に失敗しました' : '投稿に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto">
      <div className="w-full sm:max-w-2xl bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-xl shadow-2xl max-h-[95vh] overflow-y-auto">
        <div className="sm:hidden flex justify-center pt-3 pb-1 sticky top-0 bg-white dark:bg-gray-800 z-20">
          <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
        </div>
        
        <div className="sticky top-0 sm:top-auto bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-3xl sm:rounded-t-xl z-10">
          <h2 className="text-base sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <i className={`${isEditMode ? 'ri-edit-line' : 'ri-trophy-line'} text-teal-600 text-lg sm:text-2xl`}></i>
            <span className="text-sm sm:text-2xl">
              {isEditMode ? '達成報告を編集' : '達成報告を投稿'}
            </span>
          </h2>
          <button
            onClick={onCancel}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            <i className="ri-close-line text-xl sm:text-2xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-5">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-xs sm:text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              タイトル <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full min-h-[44px] sm:min-h-[48px] px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-xs sm:text-sm"
              placeholder="例: TOEIC 900点達成"
              maxLength={100}
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              カテゴリ <span className="text-red-500">*</span>
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full min-h-[44px] sm:min-h-[48px] px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-xs sm:text-sm"
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              達成日 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={achievementDate}
              onChange={(e) => setAchievementDate(e.target.value)}
              className="w-full min-h-[44px] sm:min-h-[48px] px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-xs sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              詳細説明
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full min-h-[100px] sm:min-h-[110px] px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-xs sm:text-sm resize-none"
              rows={4}
              placeholder="達成の詳細や感想を記入してください"
              maxLength={500}
            />
            <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">
              {description.length}/500文字
            </p>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              画像URL（任意）
            </label>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              className="w-full min-h-[44px] sm:min-h-[48px] px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-xs sm:text-sm"
              placeholder="https://example.com/image.jpg"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              公開範囲 <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2.5 sm:space-y-3">
              <label className={`flex items-start gap-2.5 sm:gap-3 p-3 sm:p-4 border-2 rounded-xl cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-700/50 ${visibility === 'public' ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-300 dark:border-gray-600'}`}>
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={visibility === 'public'}
                  onChange={(e) => setVisibility(e.target.value as 'public')}
                  className="mt-0.5 w-4 h-4 text-teal-600 focus:ring-teal-500 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                    <i className="ri-global-line text-teal-600 text-sm sm:text-base"></i>
                    <span className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">全体公開</span>
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 break-words">
                    Topページに表示されます。グループを選択すると、そのグループ内にも表示されます。
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-2.5 sm:gap-3 p-3 sm:p-4 border-2 rounded-xl cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-700/50 ${visibility === 'group_only' ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-gray-300 dark:border-gray-600'}`}>
                <input
                  type="radio"
                  name="visibility"
                  value="group_only"
                  checked={visibility === 'group_only'}
                  onChange={(e) => setVisibility(e.target.value as 'group_only')}
                  className="mt-0.5 w-4 h-4 text-teal-600 focus:ring-teal-500 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1">
                    <i className="ri-group-line text-teal-600 text-sm sm:text-base"></i>
                    <span className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm">グループ内限定公開</span>
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 break-words">
                    選択したグループ内のみに表示されます（Topページには表示されません）
                  </p>
                </div>
              </label>
            </div>
          </div>

          {visibility === 'group_only' && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                公開先グループ <span className="text-red-500">*</span>
              </label>
              {myGroups.length === 0 ? (
                <div className="p-3 sm:p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-xs sm:text-sm text-yellow-800 dark:text-yellow-200 break-words">
                    参加しているグループがありません。グループ内限定公開を選択する場合は、先にグループに参加してください。
                  </p>
                </div>
              ) : (
                <select
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  className="w-full min-h-[44px] sm:min-h-[48px] px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-xs sm:text-sm"
                  required={visibility === 'group_only'}
                >
                  <option value="">グループを選択してください</option>
                  {myGroups.map((group) => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {visibility === 'public' && myGroups.length > 0 && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                グループにも投稿（任意）
              </label>
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                className="w-full min-h-[44px] sm:min-h-[48px] px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:bg-gray-700 dark:text-white text-xs sm:text-sm"
              >
                <option value="">グループを選択（任意）</option>
                {myGroups.map((group) => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
              <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                グループを選択すると、Topページとそのグループの両方に表示されます
              </p>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3 pt-3 sm:pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="w-full min-h-[44px] sm:min-h-[48px] px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-xs sm:text-sm font-medium whitespace-nowrap"
              disabled={loading}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="w-full min-h-[44px] sm:min-h-[48px] px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-xs sm:text-sm font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading
                ? (isEditMode ? '更新中...' : '投稿中...')
                : (isEditMode ? '更新する' : '投稿する')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
